import React, { useState } from "react";
import {
  Container,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Input,
  Label,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { getLoggedinUser, setUserData } from "../../utils/StorageManager";
import { updateQuickStartStep, updateChannelsAdded } from "../../apiCalls/caller";
import { toast } from "react-toastify";
import { Navigate } from "react-router-dom";

// Update these with the actual YouTube video IDs when available
const VIDEO_OVERVIEW_ID = "";
const VIDEO_WALKTHROUGH_ID = "";

const TOTAL_STEPS = 4;

const CHANNEL_OPTIONS = [
  { key: "amazon", label: "Amazon" },
  { key: "ebay", label: "eBay" },
  { key: "walmart", label: "Walmart" },
];

const QuickStart = () => {
  document.title = "Quick Start | Buyers Club";

  const userProfileSession = getLoggedinUser();
  const isTrialUser = userProfileSession?.user_freeType === "trial";

  const userId = userProfileSession?.user_id;
  const savedStep = parseInt(userProfileSession?.user_quickStartStep ?? 0, 10);

  const buildInitialCompleted = () => {
    const initial = {};
    for (let s = 1; s <= Math.min(savedStep, TOTAL_STEPS); s++) {
      initial[s] = true;
    }
    return initial;
  };

  const initialOpen = savedStep < TOTAL_STEPS ? String(savedStep + 1) : "";

  const [open, setOpen] = useState(initialOpen);
  const [completedSteps, setCompletedSteps] = useState(buildInitialCompleted);
  const [saving, setSaving] = useState({});
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const toggleVideoModal = () => setVideoModalOpen((prev) => !prev);
  const [videoModal2Open, setVideoModal2Open] = useState(false);
  const toggleVideoModal2 = () => setVideoModal2Open((prev) => !prev);
  const [videoModalEbayOpen, setVideoModalEbayOpen] = useState(false);
  const toggleVideoModalEbay = () => setVideoModalEbayOpen((prev) => !prev);
  const [videoModalAmazonOpen, setVideoModalAmazonOpen] = useState(false);
  const toggleVideoModalAmazon = () => setVideoModalAmazonOpen((prev) => !prev);
  const [videoModalStep1Open, setVideoModalStep1Open] = useState(false);
  const toggleVideoModalStep1 = () => setVideoModalStep1Open((prev) => !prev);

  // Step 2 – sub-accordion (open 2B if step 2 already done)
  const [openSC, setOpenSC] = useState(savedStep >= 2 ? "sc2" : "sc1");

  // Step 2 – sub-step 1 confirmation (pre-mark if step 2 already done)
  const [profileConfirmed, setProfileConfirmed] = useState(savedStep >= 2);

  // Step 2 – channel selection (initialize from user_channelsAdded in session)
  const { channels: _initialChannels, savedOthers: _initialSavedOthers } = (() => {
    const savedChannelsStr = userProfileSession?.user_channelsAdded ?? "";
    const keys = savedChannelsStr
      ? savedChannelsStr.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
    const known = new Set(CHANNEL_OPTIONS.map((c) => c.key));
    const init = {};
    const others = [];
    for (const ck of keys) {
      if (known.has(ck)) {
        init[ck] = true;
      } else if (ck) {
        init.other = true;
        others.push(ck);
      }
    }
    return { channels: init, savedOthers: others };
  })();

  const [selectedChannels, setSelectedChannels] = useState(_initialChannels);
  const [savedOthers, setSavedOthers] = useState(_initialSavedOthers);
  const [newOtherText, setNewOtherText] = useState("");

  const toggle = (id) => setOpen(open === id ? "" : id);
  const toggleSC = (id) => setOpenSC(openSC === id ? "" : id);

  const [savingChannel, setSavingChannel] = useState(false);

  const handleChannelChange = async (key, checked) => {
    // Once step 2 is completed, no modifications allowed from QuickStart
    if (!!completedSteps[2]) return;
    // Never allow unchecking if step not yet done either (free until saved)
    setSelectedChannels((prev) => ({ ...prev, [key]: checked }));

  };

  const saveOtherChannel = async () => {
    const text = newOtherText.trim();
    if (!text) {
      toast.error("Please enter a marketplace name.", { position: "top-center", autoClose: 2000 });
      return;
    }
    const updatedOthers = [...savedOthers, text];
    const updatedChannels = { ...selectedChannels, other: true };
    const keys = CHANNEL_OPTIONS.filter((c) => updatedChannels[c.key]).map((c) => c.key);
    updatedOthers.forEach((o) => keys.push(o));
    const channels = keys.join(",");
    setSavingChannel(true);
    try {
      await updateChannelsAdded({ id: userId, channels });
      toast.success("Marketplace added!", { position: "top-center", autoClose: 2000 });
      const currentUser = getLoggedinUser();
      if (currentUser) {
        setUserData({ ...currentUser, user_channelsAdded: channels });
      }
      setSavedOthers(updatedOthers);
      setNewOtherText("");
    } catch {
      toast.error("Could not save channel. Please try again.", { position: "top-center", autoClose: 3000 });
    } finally {
      setSavingChannel(false);
    }
  };

  const getChannelsParam = () => {
    const keys = CHANNEL_OPTIONS.filter((c) => selectedChannels[c.key]).map(
      (c) => c.key
    );
    if (selectedChannels.other) {
      savedOthers.forEach((o) => keys.push(o));
      if (newOtherText.trim()) keys.push(newOtherText.trim());
    }
    return keys.join(",");
  };

  const canCompleteStep2 = () =>
    profileConfirmed && Object.values(selectedChannels).some(Boolean);

  const markStepComplete = async (step) => {
    setSaving((prev) => ({ ...prev, [step]: true }));
    try {
      if (step === 2) {
        const channels = getChannelsParam();
        await Promise.all([
          updateQuickStartStep({ id: userId, step }),
          updateChannelsAdded({ id: userId, channels }),
        ]);
        // lock profile confirmation
        setProfileConfirmed(true);
      } else {
        await updateQuickStartStep({ id: userId, step });
      }

      setCompletedSteps((prev) => ({ ...prev, [step]: true }));
      const currentUser = getLoggedinUser();
      if (currentUser) {
        const sessionUpdate = { ...currentUser, user_quickStartStep: step };
        if (step === 2) sessionUpdate.user_channelsAdded = getChannelsParam();
        setUserData(sessionUpdate);
      }

      const nextStep = step + 1;
      setOpen(nextStep <= TOTAL_STEPS ? String(nextStep) : "congrats");

      toast.success(`Step ${step} marked as completed!`, {
        position: "top-center",
        autoClose: 2000,
      });
    } catch (err) {
      toast.error("Could not save progress. Please try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setSaving((prev) => ({ ...prev, [step]: false }));
    }
  };

  const stepIcon = (step) =>
    completedSteps[step] ? (
      <i className="mdi mdi-check-circle text-success fs-5" />
    ) : (
      <i className="mdi mdi-circle-outline text-secondary fs-5" />
    );

  const stepTitleClass = (step) =>
    completedSteps[step] ? "text-decoration-line-through text-muted" : "";

  const renderFooter = (step) => {
    const isCompleted = !!completedSteps[step];
    const isSaving = !!saving[step];
    return (
      <>
        <hr />
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id={`step-check-${step}`}
            checked={isCompleted}
            disabled={isCompleted || isSaving}
            onChange={(e) => {
              if (e.target.checked) markStepComplete(step);
            }}
          />
          <label
            className="form-check-label fw-semibold"
            htmlFor={`step-check-${step}`}
          >
            {isSaving
              ? "Saving..."
              : isCompleted
              ? "Step completed ✓"
              : "Mark this step as completed"}
          </label>
        </div>
      </>
    );
  };

  const allCompleted = [1, 2, 3, 4].every((s) => !!completedSteps[s]);

  // Redirect trial users away from QuickStart (after all hooks)
  if (isTrialUser) {
    return <Navigate to="/products" replace />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Quick Start" pageTitle="Quick Start" />

        <div className="mb-3">
          <h5 className="text-muted">
            Welcome to Buyers Club Quick Start! Follow the steps below to get
            your account fully set up.
          </h5>
        </div>

        <div className="mb-4 p-3 border rounded bg-light d-flex align-items-center gap-3">
          <i className="mdi mdi-play-circle text-primary" style={{ fontSize: 36 }} />
          <div>
            <p className="text-muted mb-1">Watch a quick introduction before starting the setup steps.</p>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={toggleVideoModal}
            >
              <i className="mdi mdi-play me-1" />Watch Video
            </button>
          </div>
        </div>

        <Modal isOpen={videoModalOpen} toggle={toggleVideoModal} centered size="lg">
          <ModalHeader toggle={toggleVideoModal}>Introductory Video</ModalHeader>
          <ModalBody>
            <div className="ratio ratio-16x9">
              {videoModalOpen && (
                <video
                  src="https://d2kw2m00jcbu82.cloudfront.net/videos/bc3_-_walkthrough_angel_1080.mp4"
                  controls
                  autoPlay
                  style={{ width: "100%", height: "100%" }}
                />
              )}
            </div>
          </ModalBody>
        </Modal>

        <Accordion open={open} toggle={toggle}>
          {/* ── STEP 1 ── */}
          <AccordionItem>
            <AccordionHeader targetId="1">
              <span className="d-flex align-items-center gap-2 w-100">
                {stepIcon(1)}
                <span className={stepTitleClass(1)}>
                  Step #1 – Becoming a Buyers Club Member
                </span>
              </span>
            </AccordionHeader>
            <AccordionBody accordionId="1">
              <div className="ps-2">
                <p>
                  Congratulations! Your Buyers Club Account should have been set
                  up automatically when you paid for membership.
                </p>
                <p>
                  If your password creation process was interrupted and you need
                  to complete this step, please contact support to reset your
                  password.
                </p>
                <p className="text-muted">
                  Please check your SPAM folder for your welcome email.
                </p>



                {VIDEO_OVERVIEW_ID && (
                  <>
                    <h6 className="mt-3">
                      Watch the Quick Start Overview Video First…
                    </h6>
                    <div className="ratio ratio-16x9 mb-3">
                      <iframe
                        src={`https://www.youtube.com/embed/${VIDEO_OVERVIEW_ID}`}
                        title="Quick Start Overview"
                        allowFullScreen
                      />
                    </div>
                  </>
                )}

                {VIDEO_WALKTHROUGH_ID && (
                  <>
                    <h6 className="mt-3">Watch Your Buyers Club Walkthrough</h6>
                    <p className="text-muted">
                      Full Sign-In Info and All The Resources You Need!
                    </p>
                    <div className="ratio ratio-16x9 mb-3">
                      <iframe
                        src={`https://www.youtube.com/embed/${VIDEO_WALKTHROUGH_ID}`}
                        title="Buyers Club Walkthrough"
                        allowFullScreen
                      />
                    </div>
                  </>
                )}
              </div>
              {renderFooter(1)}
            </AccordionBody>
          </AccordionItem>

          {/* ── STEP 2 ── */}
          <AccordionItem>
            <AccordionHeader targetId="2">
              <span className="d-flex align-items-center gap-2 w-100">
                {stepIcon(2)}
                <span className={stepTitleClass(2)}>
                  Step #2 – Set up your Sellercloud Account
                </span>
              </span>
            </AccordionHeader>
            <AccordionBody accordionId="2">
              <div className="ps-2">
                <p>
                  Wait until you receive your email with your Seller Cloud login
                  info to get started.
                </p>
                <p>
                  Your Sellercloud Account is where you will connect and manage
                  your marketplaces, listings and inventory levels.
                </p>

                {/* Sub-accordion – Sellercloud steps */}
                <Accordion open={openSC} toggle={toggleSC} className="mb-4">
                  <AccordionItem>
                    <AccordionHeader targetId="sc1">
                      Step 2A – Sellercloud Settings
                    </AccordionHeader>
                    <AccordionBody accordionId="sc1">
                      <p>
                        Log into Seller Cloud with your credentials emailed to
                        you and follow the guide to complete your company
                        profile. 
                        <button
                          type="button"
                          className="btn btn-primary btn-sm ms-3"
                          onClick={toggleVideoModal2}
                        >
                          <i className="mdi mdi-play me-1" />Watch Video
                        </button>
                      </p>
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <a
                          href="https://buyersclub.sellercloud.com/"
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-outline-primary btn-sm"
                        >
                          Seller Cloud Login
                        </a>
                      </div>

                      <div className="border rounded p-3 bg-light mb-3">
                        <h6 className="mb-1">Senderoo Warehousing</h6>
                        <p className="mb-0">
                          4385 Seymour Highway<br />
                          Wichita Falls, TX 76309<br />
                          (940) 249-4198
                        </p>
                        <a
                          href="https://senderoo.com/"
                          target="_blank"
                          rel="noreferrer"
                        >
                          senderoo.com
                        </a>
                      </div>

                      <Modal isOpen={videoModal2Open} toggle={toggleVideoModal2} centered size="lg">
                        <ModalHeader toggle={toggleVideoModal2}>Sellercloud Settings Video</ModalHeader>
                        <ModalBody>
                          <div className="ratio ratio-16x9">
                            {videoModal2Open && (
                              <video
                                src="https://d2kw2m00jcbu82.cloudfront.net/videos/complete_profile_company_sc_1080.mp4"
                                controls
                                autoPlay
                                style={{ width: "100%", height: "100%" }}
                              />
                            )}
                          </div>
                        </ModalBody>
                      </Modal>

                      <FormGroup check className="mt-2">
                        <Input
                          type="checkbox"
                          id="sc1-profile-confirmed"
                          checked={profileConfirmed}
                          disabled={!!completedSteps[2] || profileConfirmed}
                          onChange={(e) => {
                            if (e.target.checked) setProfileConfirmed(true);
                          }}
                        />
                        <Label check htmlFor="sc1-profile-confirmed" className="fw-semibold">
                          I have completed my company profile in Seller Cloud
                        </Label>
                      </FormGroup>
                    </AccordionBody>
                  </AccordionItem>

                  <AccordionItem>
                    <AccordionHeader targetId="sc2">
                      Step 2B – Connect At Least One Marketplace
                    </AccordionHeader>
                    <AccordionBody accordionId="sc2">
                      <p>
                        You will be able to connect more marketplaces after
                        initial setup.
                      </p>
                      <p>
                        <strong>eBay:</strong> In Seller Cloud connect your
                        eBay seller account if applicable.
                        <button
                          type="button"
                          className="btn btn-primary btn-sm ms-2"
                          onClick={toggleVideoModalEbay}
                        >
                          <i className="mdi mdi-play me-1" />Watch Video
                        </button>
                      </p>
                      <p>
                        <strong>Amazon:</strong> In Seller Cloud connect your
                        Amazon seller account if applicable.
                        <button
                          type="button"
                          className="btn btn-primary btn-sm ms-2"
                          onClick={toggleVideoModalAmazon}
                        >
                          <i className="mdi mdi-play me-1" />Watch Video
                        </button>
                      </p>

                      <Modal isOpen={videoModalEbayOpen} toggle={toggleVideoModalEbay} centered size="lg">
                        <ModalHeader toggle={toggleVideoModalEbay}>eBay Setup Video</ModalHeader>
                        <ModalBody>
                          <div className="ratio ratio-16x9">
                            {videoModalEbayOpen && (
                              <video
                                src="https://d2kw2m00jcbu82.cloudfront.net/videos/bc3-sc-e_bay-connection2_1080.mp4"
                                controls
                                autoPlay
                                style={{ width: "100%", height: "100%" }}
                              />
                            )}
                          </div>
                        </ModalBody>
                      </Modal>

                      <Modal isOpen={videoModalAmazonOpen} toggle={toggleVideoModalAmazon} centered size="lg">
                        <ModalHeader toggle={toggleVideoModalAmazon}>Amazon Setup Video</ModalHeader>
                        <ModalBody>
                          <div className="ratio ratio-16x9">
                            {videoModalAmazonOpen && (
                              <video
                                src="https://d2kw2m00jcbu82.cloudfront.net/videos/bc3-sc-amz-connection_1080.mp4"
                                controls
                                autoPlay
                                style={{ width: "100%", height: "100%" }}
                              />
                            )}
                          </div>
                        </ModalBody>
                      </Modal>

                      <div className="border rounded p-3 bg-light mt-3">
                        <p className="fw-semibold mb-2">
                          Select the marketplace(s) you connected. You must
                          select at least one to complete this step:
                        </p>
                        {CHANNEL_OPTIONS.map(({ key, label }) => (
                          <FormGroup check key={key} className="mb-1">
                            <Input
                              type="checkbox"
                              id={`channel-${key}`}
                              checked={!!selectedChannels[key]}
                              disabled={savingChannel || !!completedSteps[2]}
                              onChange={(e) =>
                                handleChannelChange(key, e.target.checked)
                              }
                            />
                            <Label check htmlFor={`channel-${key}`}>
                              {label}
                            </Label>
                          </FormGroup>
                        ))}
                        <FormGroup check className="mb-1">
                          <Input
                            type="checkbox"
                            id="channel-other"
                            checked={!!selectedChannels.other}
                            disabled={savingChannel || !!completedSteps[2]}
                            onChange={(e) =>
                              handleChannelChange("other", e.target.checked)
                            }
                          />
                          <Label check htmlFor="channel-other">
                            Other{savedOthers.length > 0 && ":"}
                            {savedOthers.map((name, idx) => (
                              <span key={idx} className="badge bg-soft-primary text-primary ms-1">
                                {name}
                              </span>
                            ))}
                          </Label>
                        </FormGroup>
                        {selectedChannels.other && !completedSteps[2] && (
                          <div className="d-flex align-items-center gap-2 mt-1 ms-4">
                            <Input
                              type="text"
                              style={{ maxWidth: 300 }}
                              placeholder="Specify marketplace…"
                              value={newOtherText}
                              onChange={(e) => setNewOtherText(e.target.value)}
                            />
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              disabled={savingChannel || !newOtherText.trim()}
                              onClick={saveOtherChannel}
                            >
                              {savingChannel ? "Saving..." : "Save"}
                            </button>
                          </div>
                        )}
                      </div>
                    </AccordionBody>
                  </AccordionItem>

                </Accordion>
              </div>

              <hr />
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="step-check-2"
                  checked={!!completedSteps[2]}
                  disabled={
                    !!completedSteps[2] || !!saving[2] || !canCompleteStep2()
                  }
                  onChange={(e) => {
                    if (e.target.checked) markStepComplete(2);
                  }}
                />
                <label
                  className="form-check-label fw-semibold"
                  htmlFor="step-check-2"
                >
                  {saving[2]
                    ? "Saving..."
                    : completedSteps[2]
                    ? "Step completed ✓"
                    : !canCompleteStep2()
                    ? "Select at least one marketplace to complete this step"
                    : "Mark this step as completed"}
                </label>
              </div>
            </AccordionBody>
          </AccordionItem>

          {/* ── STEP 3 ── */}
          <AccordionItem>
            <AccordionHeader targetId="3">
              <span className="d-flex align-items-center gap-2 w-100">
                {stepIcon(3)}
                <span className={stepTitleClass(3)}>
                  Step #3 – Gain Access to Buyer's Club Learning Hub
                </span>
              </span>
            </AccordionHeader>
            <AccordionBody accordionId="3">
              <div className="ps-2">
                <p>This is your learning hub for training and updates.</p>
                <p>
                  Once the above steps are complete, you will receive an email
                  with login credentials.
                </p>
                <p>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() =>
                      window.open(
                        "https://www.mybuyersclubaccess.net/",
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  >
                    Learning Hub
                  </button>
                </p>
              </div>
              {renderFooter(3)}
            </AccordionBody>
          </AccordionItem>

          {/* ── STEP 4 ── */}
          <AccordionItem>
            <AccordionHeader targetId="4">
              <span className="d-flex align-items-center gap-2 w-100">
                {stepIcon(4)}
                <span className={stepTitleClass(4)}>
                  Step #4 – Set Up your Warehouse Account with Senderoo
                </span>
              </span>
            </AccordionHeader>
            <AccordionBody accordionId="4">
              <div className="ps-2">
                <p>
                  Your Warehouse account will allow you to store inventory and
                  automate logistics.
                </p>
                <p>
                  Click the button below to get signed up with our Warehouse
                  partner, Senderoo.
                </p>
                <p>
                  <a
                    href="https://docs.google.com/forms/d/e/1FAIpQLSfPWf8pwvULVhwMMYa4j5ZBQRJa9GiPLC2gC9FweqcRURj_gQ/viewform?pli=1"
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary btn-sm"
                  >
                    Sign Up Here
                  </a>
                </p>
              </div>
              {renderFooter(4)}
            </AccordionBody>
          </AccordionItem>

          {/* ── CONGRATULATIONS ── */}
          {allCompleted && (
            <AccordionItem>
              <AccordionHeader targetId="congrats">
                <span className="d-flex align-items-center gap-2 w-100">
                  <i className="mdi mdi-check-decagram text-success fs-5" />
                  <span className="fw-semibold text-success">
                    Congratulations – All steps completed!
                  </span>
                </span>
              </AccordionHeader>
              <AccordionBody accordionId="congrats">
                <div className="text-center py-4">
                  <i
                    className="mdi mdi-check-decagram text-success"
                    style={{ fontSize: 72 }}
                  />
                  <h2 className="mt-3">Congratulations!</h2>
                  <p className="text-muted fs-5 mb-1">
                    You have successfully completed all Quick Start steps.
                  </p>
                  <p className="text-muted">
                    Your account is fully set up and ready to go. You can now
                    start browsing and purchasing products. Welcome to the
                    Buyers Club!
                  </p>
                </div>
              </AccordionBody>
            </AccordionItem>
          )}
        </Accordion>
      </Container>
    </div>
  );
};

export default QuickStart;
